import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, History } from "lucide-react";
import { formatDateTime } from "@/lib/datetime";
import { Card, Timeline, gbp } from "./AnalyticsParts";
import { Purchases, Engagement, Conversations, Tracking } from "./AnalyticsSections";
import { AiInsights } from "./AiInsights";
import { ProductsOwned } from "./ProductsOwned";

const Metric = ({ label, value, sub, testid }) => (
  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4" data-testid={testid}>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
    {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

export function CustomerAnalytics({ customerId }) {
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/customers/${customerId}/analytics`).then((r) => setA(r.data)).catch((e) => setErr(e.response?.data?.detail || "Could not load analytics"));
  }, [customerId]);
  if (err) return <p className="text-sm text-rose-600" data-testid="analytics-error">{err}</p>;
  if (!a) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  const s = a.summary;
  return (
    <div className="space-y-6" data-testid="customer-analytics">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Metric label="Total spent" value={gbp(s.total_spend)} sub={`Orders ${gbp(s.order_spend)} · invoices paid ${gbp(s.invoice_paid)}`} testid="analytics-total-spend" />
        <Metric label="Orders" value={s.orders} sub={s.avg_order ? `Avg ${gbp(s.avg_order)}` : "No completed orders"} testid="analytics-orders" />
        <Metric label="Jobs · Enquiries" value={`${s.jobs} · ${s.enquiries}`} testid="analytics-jobs" />
        <Metric label="Quotes" value={`${s.quotes_accepted}/${s.quotes} accepted`} sub={s.accepted_value ? `${gbp(s.accepted_value)} accepted` : null} testid="analytics-quotes" />
        <Metric label="Last activity" value={s.last_activity ? formatDateTime(s.last_activity).split(",")[0] : "–"} sub={s.first_activity ? `Customer since ${formatDateTime(s.first_activity).split(",")[0]}` : null} testid="analytics-last-activity" />
      </div>
      {s.name_only_links > 0 && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="analytics-name-link-note">{s.name_only_links} record(s) are linked by exact customer name only (marked "name match"). Check them if this name is common.</p>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <ProductsOwned customerId={customerId} />
          <Purchases orders={a.orders} />
          <Card title="Activity timeline" icon={History} testid="analytics-timeline-card"><Timeline items={a.timeline} /></Card>
        </div>
        <div className="space-y-6">
          <AiInsights customerId={customerId} initial={a.insights} />
          <Engagement a={a} />
          <Conversations items={a.conversations} />
          <Tracking items={a.tracking} />
        </div>
      </div>
    </div>
  );
}
